import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, FileDownIcon, LockIcon, UnlockIcon, SearchIcon, PrinterIcon, EyeIcon, PlusIcon, ClipboardListIcon, EditIcon, TrashIcon } from 'lucide-react';
import Tooltip from '../common/Tooltip';
import StatusBadge from '../common/StatusBadge';
import ConfirmModal from '../modals/ConfirmModal';
import { format } from 'date-fns';
import ViewTicketDetailsModal from '../modals/ViewTicketDetailsModal';
import BatchTicketEntryModal from '../modals/BatchTicketEntryModal';
import SingleTicketEntryModal from '../modals/SingleTicketEntryModal';
import EditTicketModal from '../modals/EditTicketModal';
import ConfirmDeleteModal from '../modals/ConfirmDeleteModal';
interface TicketGroupDetailProps {
  group: {
    id: string;
    tourCode: string;
    createdBy: string;
    createdAt: string;
    refNo: string;
    ticketCount: number;
    totalAmount: number;
    status: string;
  };
  onBack: () => void;
}
// Mock ticket data
const mockTickets = [{
  id: 1,
  ticketNumber: '1601234567890',
  paxName: 'LOH/POHLEE MS',
  sector: 'SIN/CAI/SIN',
  sectorCode: 'SINCAI',
  airlineCode: 'QR',
  class: 'G',
  issueDate: '2023-06-15',
  airFare: 1200.0,
  tax: 300.0,
  totalCost: 1500.0,
  remarks: 'VIP passenger',
  reconciled: true
}, {
  id: 2,
  ticketNumber: '1601234567891',
  paxName: 'WONG/MARY MRS',
  sector: 'SIN/CAI/SIN',
  sectorCode: 'SINCAI',
  airlineCode: 'QR',
  class: 'G',
  issueDate: '2023-06-15',
  airFare: 1200.0,
  tax: 300.0,
  totalCost: 1500.0,
  remarks: '',
  reconciled: false
}, {
  id: 3,
  ticketNumber: '1601234567892',
  paxName: 'TAN/JOHN MR',
  sector: 'SIN/CAI/SIN',
  sectorCode: 'SINCAI',
  airlineCode: 'QR',
  class: 'G',
  issueDate: '2023-06-16',
  airFare: 1200.0,
  tax: 300.0,
  totalCost: 1500.0,
  remarks: 'Frequent flyer',
  reconciled: false,
  type: 'ATC',
  referenceNumber: '12XCAI1207',
  tourCode: '12XCAI1207/23QR',
  staffResponsible: 'Jane Smith'
}];
const TicketGroupDetail: React.FC<TicketGroupDetailProps> = ({
  group,
  onBack
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'lock' | 'unlock'>('lock');
  const [isLocked, setIsLocked] = useState(group.status === 'Locked');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
  const [isSingleTicketModalOpen, setIsSingleTicketModalOpen] = useState(false);
  const navigate = useNavigate();
  // Format date in DD MMM YYYY format
  const formatDate = dateString => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'dd MMM yyyy');
    } catch (e) {
      return dateString;
    }
  };
  // Handle toggling lock status
  const handleToggleLock = () => {
    setConfirmAction(isLocked ? 'unlock' : 'lock');
    setIsConfirmModalOpen(true);
  };
  // Handle confirming lock/unlock action
  const confirmToggleLock = () => {
    console.log(`${confirmAction === 'lock' ? 'Locking' : 'Unlocking'} group ${group.id}`);
    // Implement actual lock/unlock functionality here
    setIsLocked(confirmAction === 'lock');
    setIsConfirmModalOpen(false);
  };
  // Handle viewing ticket details
  const handleViewTicket = ticket => {
    setSelectedTicket(ticket);
    setIsViewModalOpen(true);
  };
  // Handle editing ticket
  const handleEditTicket = ticket => {
    setSelectedTicket(ticket);
    setIsEditModalOpen(true);
  };
  // Handle deleting ticket
  const handleDeleteTicket = ticket => {
    setSelectedTicket(ticket);
    setIsDeleteModalOpen(true);
  };
  // Confirm delete ticket
  const confirmDeleteTicket = () => {
    console.log(`Deleting ticket: ${selectedTicket?.ticketNumber}`);
    // Implement actual delete functionality here
    setIsDeleteModalOpen(false);
    setSelectedTicket(null);
  };
  // Handle updating a ticket
  const handleUpdateTicket = updatedTicket => {
    console.log('Updating ticket:', updatedTicket);
    // Implement actual update functionality here
    setIsEditModalOpen(false);
    setSelectedTicket(null);
  };
  // Handle adding a new ticket
  const handleAddTicket = ticketData => {
    console.log('Adding new ticket:', ticketData);
    // Implement actual add functionality here
    setIsSingleTicketModalOpen(false);
  };
  // Handle printing
  const handlePrint = () => {
    window.print();
  };
  // Mock ticket data
  const filteredTickets = mockTickets.filter(ticket => ticket.ticketNumber.includes(searchTerm) || ticket.paxName.toLowerCase().includes(searchTerm.toLowerCase()));
  // Calculate totals
  const totals = {
    airFare: filteredTickets.reduce((sum, ticket) => sum + ticket.airFare, 0),
    tax: filteredTickets.reduce((sum, ticket) => sum + ticket.tax, 0),
    totalCost: filteredTickets.reduce((sum, ticket) => sum + ticket.totalCost, 0)
  };
  return <div className="space-y-6" data-id="element-836">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-837">
        <div className="flex items-center gap-4" data-id="element-838">
          <button onClick={onBack} className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-md" aria-label="Go back to ticket groups" data-id="element-839">
            <ArrowLeftIcon className="h-5 w-5" data-id="element-840" />
          </button>
          <h2 className="text-xl font-bold text-gray-900" data-id="element-841">
            Ticket Group: {group.id}
          </h2>
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-842">
          <Tooltip content={isLocked ? 'Unlock Group' : 'Lock Group'} data-id="element-843">
            <button className={`inline-flex items-center px-4 py-2 ${isLocked ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-600 hover:bg-gray-700'} text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`} onClick={handleToggleLock} aria-label={isLocked ? 'Unlock Group' : 'Lock Group'} data-id="element-844">
              {isLocked ? <UnlockIcon className="h-5 w-5 mr-1" data-id="element-845" /> : <LockIcon className="h-5 w-5 mr-1" data-id="element-846" />}
              <span data-id="element-847">{isLocked ? 'Unlock Group' : 'Lock Group'}</span>
            </button>
          </Tooltip>
          <Tooltip content="Export Tickets" data-id="element-848">
            <button className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" aria-label="Export tickets" data-id="element-849">
              <FileDownIcon className="h-5 w-5 mr-1" data-id="element-850" />
              <span data-id="element-851">Export Tickets</span>
            </button>
          </Tooltip>
          <Tooltip content="Print Group Details" data-id="element-852">
            <button className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" onClick={handlePrint} aria-label="Print group details" data-id="element-853">
              <PrinterIcon className="h-5 w-5 mr-1" data-id="element-854" />
              <span data-id="element-855">Print</span>
            </button>
          </Tooltip>
        </div>
      </div>
      <div className="bg-white shadow rounded-lg p-6" data-id="element-856">
        <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-857">
          Group Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-858">
          <div data-id="element-859">
            <div className="mb-4" data-id="element-860">
              <p className="text-sm text-gray-500" data-id="element-861">Group ID</p>
              <p className="font-medium" data-id="element-862">{group.id}</p>
            </div>
            <div data-id="element-863">
              <p className="text-sm text-gray-500" data-id="element-864">Created By</p>
              <p className="font-medium" data-id="element-865">{group.createdBy}</p>
            </div>
          </div>
          <div data-id="element-866">
            <div className="mb-4" data-id="element-867">
              <p className="text-sm text-gray-500" data-id="element-868">Tour Code</p>
              <p className="font-medium" data-id="element-869">{group.tourCode}</p>
            </div>
            <div data-id="element-870">
              <p className="text-sm text-gray-500" data-id="element-871">Created Date</p>
              <p className="font-medium" data-id="element-872">{formatDate(group.createdAt)}</p>
            </div>
          </div>
          <div data-id="element-873">
            <div className="mb-4" data-id="element-874">
              <p className="text-sm text-gray-500" data-id="element-875">Request Ref</p>
              <p className="font-medium" data-id="element-876">{group.refNo}</p>
            </div>
            <div className="flex items-center" data-id="element-877">
              <p className="text-sm text-gray-500 mr-2" data-id="element-878">Status:</p>
              {isLocked ? <Tooltip content="Locked – no further edits allowed" data-id="element-879">
                  <StatusBadge status="locked" label="Locked" showIcon={true} data-id="element-880" />
                </Tooltip> : <Tooltip content="Unlocked – editing allowed" data-id="element-881">
                  <StatusBadge status="success" label="Open" showIcon={true} data-id="element-882" />
                </Tooltip>}
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-883">
        <h2 className="text-xl font-bold text-gray-900" data-id="element-884">Tickets Records</h2>
        <div className="flex flex-wrap gap-2" data-id="element-885">
          <button className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" onClick={() => setIsSingleTicketModalOpen(true)} disabled={isLocked} data-id="element-886">
            <PlusIcon className="h-5 w-5 mr-1" data-id="element-887" />
            <span data-id="element-888">Add Single Ticket</span>
          </button>
          <button className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" onClick={() => setIsBatchModalOpen(true)} disabled={isLocked} data-id="element-889">
            <ClipboardListIcon className="h-5 w-5 mr-1" data-id="element-890" />
            <span data-id="element-891">Batch Add Tickets</span>
          </button>
        </div>
      </div>
      <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-892">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-893">
          <h3 className="text-lg font-medium text-gray-900" data-id="element-894">Tickets Records</h3>
          <div className="w-1/3" data-id="element-895">
            <div className="relative" data-id="element-896">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-897">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-898" />
              </div>
              <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Search tickets..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} aria-label="Search tickets in group" data-id="element-899" />
            </div>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-900">
          <table className="min-w-full divide-y divide-gray-200" aria-label="Tickets in group" data-id="element-901">
            <thead className="bg-gray-50 sticky top-0 z-10" data-id="element-902">
              <tr data-id="element-903">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-904">
                  Ticket Number
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-905">
                  Passenger Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-906">
                  Sector
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-907">
                  Airline
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-908">
                  Class
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-909">
                  Issue Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-910">
                  Air Fare
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-911">
                  Tax
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-912">
                  Total Cost
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-913">
                  Reconciliation
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-914">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-915">
              {filteredTickets.map((ticket, index) => <tr key={ticket.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 ${ticket.type === 'ATC' ? 'bg-amber-50 hover:bg-amber-100' : ''}`} data-id="element-916">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-917">
                    {ticket.ticketNumber}
                    {ticket.type === 'ATC' && <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800" data-id="element-918">
                        ATC
                      </span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-919">
                    {ticket.paxName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-920">
                    {ticket.sector}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-921">
                    {ticket.airlineCode}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-922">
                    {ticket.class}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-923">
                    {formatDate(ticket.issueDate)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-924">
                    ${ticket.airFare.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-925">
                    ${ticket.tax.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-926">
                    ${ticket.totalCost.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-927">
                    <StatusBadge status={ticket.reconciled ? 'success' : 'pending'} label={ticket.reconciled ? 'Reconciled' : 'Pending'} data-id="element-928" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2" data-id="element-929">
                    {!isLocked && <>
                        <button onClick={() => handleEditTicket(ticket)} className="text-blue-600 hover:text-blue-900" aria-label={`Edit ticket ${ticket.ticketNumber}`} data-id="element-930">
                          <EditIcon className="h-4 w-4 inline" data-id="element-931" />
                        </button>
                        <button onClick={() => handleDeleteTicket(ticket)} className="text-red-600 hover:text-red-900" aria-label={`Delete ticket ${ticket.ticketNumber}`} data-id="element-932">
                          <TrashIcon className="h-4 w-4 inline" data-id="element-933" />
                        </button>
                      </>}
                    <button onClick={() => handleViewTicket(ticket)} className="text-green-600 hover:text-green-900" aria-label={`View ticket ${ticket.ticketNumber} details`} data-id="element-934">
                      <EyeIcon className="h-4 w-4 inline" data-id="element-935" />
                    </button>
                  </td>
                </tr>)}
            </tbody>
            <tfoot className="bg-gray-100" data-id="element-936">
              <tr data-id="element-937">
                <td colSpan={6} className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right" data-id="element-938">
                  Summary Totals:
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-939">
                  ${totals.airFare.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-940">
                  ${totals.tax.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-941">
                  ${totals.totalCost.toFixed(2)}
                </td>
                <td colSpan={2} data-id="element-942"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      {/* Modals */}
      {isConfirmModalOpen && <ConfirmModal title={confirmAction === 'lock' ? 'Lock Ticket Group' : 'Unlock Ticket Group'} message={confirmAction === 'lock' ? 'Are you sure you want to lock this group? Locked groups cannot be edited.' : 'Are you sure you want to unlock this group? This will allow further edits.'} confirmLabel={confirmAction === 'lock' ? 'Lock Group' : 'Unlock Group'} confirmButtonClass={confirmAction === 'lock' ? 'bg-gray-600 hover:bg-gray-700' : 'bg-blue-600 hover:bg-blue-700'} onConfirm={confirmToggleLock} onCancel={() => setIsConfirmModalOpen(false)} data-id="element-943" />}
      {isViewModalOpen && selectedTicket && <ViewTicketDetailsModal onClose={() => {
      setIsViewModalOpen(false);
      setSelectedTicket(null);
    }} ticket={selectedTicket} isLocked={isLocked} data-id="element-944" />}
      {isEditModalOpen && selectedTicket && <EditTicketModal onClose={() => {
      setIsEditModalOpen(false);
      setSelectedTicket(null);
    }} onSave={handleUpdateTicket} ticket={selectedTicket} data-id="element-945" />}
      {isDeleteModalOpen && selectedTicket && <ConfirmDeleteModal onClose={() => {
      setIsDeleteModalOpen(false);
      setSelectedTicket(null);
    }} onConfirm={confirmDeleteTicket} title="Delete Ticket" message="Are you sure you want to delete this ticket? This action cannot be undone." itemName={`${selectedTicket.ticketNumber} - ${selectedTicket.paxName}`} data-id="element-946" />}
      {isBatchModalOpen && <BatchTicketEntryModal onClose={() => setIsBatchModalOpen(false)} groupId={group.id} data-id="element-947" />}
      {isSingleTicketModalOpen && <SingleTicketEntryModal onClose={() => setIsSingleTicketModalOpen(false)} groupId={group.id} onSave={handleAddTicket} data-id="element-948" />}
    </div>;
};
export default TicketGroupDetail;